import { useSignature } from 'legacy/lut/hooks/useSignature'
import { FC, useCallback, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components'
import { Switch } from 'core-components/library/checkbox'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'

import { IPartnerFlag } from '../../models/IPartnerFlag'

import { DisablingWarningDialog } from './disablingWarningDialog/DisablingWarningDialog'
import { IPartnerFlagsSwitchersProps } from './models/iPartnerFlagsSwitchersProps'

import styles from './PartnerFlags.module.scss'

export const PartnerFlags: FC<IPartnerFlagsSwitchersProps> = ({
  isEditMode,
  partnerFlags,
  onUpdateFlag,
}) => {
  /** -------------------- State ----------------------- */

  const [readonlyFlagWarning, setReadonlyFlagWarning] = useState<string>()
  const [disablingPartnerFlag, setDisablingPartnerFlag] = useState<IPartnerFlag>()

  /** -------------------- Hooks ----------------------- */

  const { dropSignature } = useSignature()

  /** -------------------- Variables ----------------------- */

  const filteredPartnerFlags = useMemo(
    () => partnerFlags.filter((flag) => flag.isVisible),
    [partnerFlags]
  )

  /** -------------------- User actions handlers ----------------------- */

  const handleChangePartnerFlag = useCallback(
    async (partnerFlag: IPartnerFlag, isEnabled: boolean, comment: string) => {
      await onUpdateFlag(partnerFlag.tag, isEnabled, comment)
      if (isEnabled) {
        await dropSignature('Financial & SOB Targets')
      }
    },
    [onUpdateFlag, dropSignature]
  )

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.partnerFlags}>
      {filteredPartnerFlags.map((partnerFlag, flagIdx) => {
        const { tag } = partnerFlag
        const switcherTag = `${tag}-switcher`
        const switchWarningMessage = `${tag}-message`
        const comment = partnerFlag.disablingComment
        const user = partnerFlag.disablingUser
        const isShowComment = !partnerFlag.isEnabled && comment
        const tooltipTag = isShowComment ? `${tag}-comment-tooltip` : `${tag}-tooltip`

        const replaceParams = [
          { key: '{comment}', value: comment || '' },
          { key: '{user}', value: user || '' },
        ]

        return (
          <div key={`${tag}${flagIdx}`} className={styles.partnerFlags__switchContainer}>
            <Tooltip
              tipContent={() => (
                <Translation text={tooltipTag} replaceParams={replaceParams} span />
              )}
            >
              <div className={styles.partnerFlags__switch}>
                <Switch
                  padding
                  disabled={!isEditMode}
                  checked={partnerFlag.isEnabled}
                  onChange={async (isEnabled) => {
                    if (partnerFlag.isReadonly) {
                      return setReadonlyFlagWarning(switchWarningMessage)
                    }

                    if (isEnabled) {
                      await handleChangePartnerFlag(partnerFlag, isEnabled, '')
                    } else {
                      setDisablingPartnerFlag(partnerFlag)
                    }
                  }}
                />
                <div className={styles.partnerFlags__switchText}>
                  <Translation span text={switcherTag} />
                </div>
              </div>
            </Tooltip>
          </div>
        )
      })}

      {/** -------------------- Dialogs ----------------------- */}

      <ModalDialog
        show={!!readonlyFlagWarning}
        size="xs"
        onClose={() => setReadonlyFlagWarning(undefined)}
        content={() => (readonlyFlagWarning ? <Translation text={readonlyFlagWarning} /> : <></>)}
      />

      <DisablingWarningDialog
        isShowDialog={!!disablingPartnerFlag?.tag}
        disablingComment={''}
        minCommentLength={5}
        flagTag={disablingPartnerFlag?.tag ?? ''}
        onApply={async (comment: any) => {
          setDisablingPartnerFlag(undefined)

          if (disablingPartnerFlag && comment) {
            await handleChangePartnerFlag(disablingPartnerFlag, false, comment)
          }
        }}
        onCancel={() => {
          setDisablingPartnerFlag(undefined)
        }}
      />
    </div>
  )
}
